/**
 * Environment configuration
 * Centralizes all environment variables with type safety
 */

interface EnvironmentConfig {
  // API Configuration
  backendUrl: string;
  
  // Auth0 Configuration
  auth0Domain: string;
  auth0ClientId: string;
  auth0Audience: string;
  auth0RedirectUri: string;

  // External Links
  gptUrl: string;
  
  // Feature Flags
  isDevelopment: boolean;
  isProduction: boolean;
  isBetaMode: boolean;
  
  // App Metadata
  appVersion: string;
}

function parseBooleanFlag(value: string | undefined, defaultValue = false): boolean {
  if (!value) {
    return defaultValue;
  }

  return ['1', 'true', 'yes', 'on'].includes(value.toLowerCase());
}

/**
 * Validates that required environment variables are present
 */
function validateEnv(): void {
  const required: (keyof ImportMetaEnv)[] = [
    'VITE_BACKEND_URL',
    'VITE_AUTH0_DOMAIN',
    'VITE_AUTH0_CLIENT_ID',
    'VITE_AUTH0_AUDIENCE'
  ];

  const missing = required.filter(key => !import.meta.env[key]);

  if (missing.length > 0) {
    throw new Error(
      `Missing required environment variables: ${missing.join(', ')}\n` +
      'Please check your .env file.'
    );
  }
}

// Validate environment on module load
if (import.meta.env.MODE !== 'test') {
  validateEnv();
}

/**
 * Exported environment configuration
 */
export const env: EnvironmentConfig = {
  // API
  backendUrl: import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000',
  
  // Auth0
  auth0Domain: import.meta.env.VITE_AUTH0_DOMAIN || '',
  auth0ClientId: import.meta.env.VITE_AUTH0_CLIENT_ID || '',
  auth0Audience: import.meta.env.VITE_AUTH0_AUDIENCE || '',
  auth0RedirectUri: window.location.origin,
  gptUrl: import.meta.env.VITE_GPT_URL || '',
  
  // Environment
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
  isBetaMode: parseBooleanFlag(import.meta.env.VITE_BETA_MODE, false),
  
  // App
  appVersion: import.meta.env.VITE_APP_VERSION || '1.0.0',
};

/**
 * Type-safe environment variable access
 */
export default env;
